import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, extractErrorMessage } from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemo, setIsDemo] = useState(() => localStorage.getItem('is_demo') === 'true');
  const [demoSessionsRemaining, setDemoSessionsRemaining] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
          setIsDemo(localStorage.getItem('is_demo') === 'true');
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          // Sync demo flag from user profile
          if (response.data.is_demo) {
            localStorage.setItem('is_demo', 'true');
            setIsDemo(true);
          }
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('is_demo');
          setUser(null);
          setIsDemo(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData, is_demo, demo_sessions_remaining } = response.data;

      try {
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (storageErr) {
        // Safari private mode throws QuotaExceededError on localStorage writes.
        // Use sessionStorage as fallback so the session still works for this tab.
        sessionStorage.setItem('token', access_token);
        sessionStorage.setItem('user', JSON.stringify(userData));
      }
      setUser(userData);

      if (is_demo) {
        localStorage.setItem('is_demo', 'true');
        setIsDemo(true);
        setDemoSessionsRemaining(demo_sessions_remaining);
      } else {
        localStorage.removeItem('is_demo');
        setIsDemo(false);
        setDemoSessionsRemaining(null);
      }

      return { success: true, is_demo, demo_sessions_remaining };
    } catch (err) {
      const message = extractErrorMessage(err, 'Login failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      const { access_token, user: newUser } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);

      return { success: true };
    } catch (err) {
      const message = extractErrorMessage(err, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedStore');
    localStorage.removeItem('is_demo');
    setUser(null);
    setIsDemo(false);
    setDemoSessionsRemaining(null);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isDemo,
    demoSessionsRemaining,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
